/**
 * Pure helper backing the V2 per-question "unsaved" indicator for tag-prefix and
 * custom-field mappings.
 *
 * These mappings live in the ROOT `tagGenerationSettings.customFormFieldTagSettings`
 * and `customFieldMap` arrays (keyed by `fieldId`), NOT in the question's own RHF
 * subtree. The V2 editor derives a question's dirty state from RHF's question-scoped
 * `dirtyFields`, which never sees those root arrays — so adding/removing/changing a
 * tag or custom-field used to flip the form-level banner (via `shouldDirty`) WITHOUT
 * flipping the per-question indicator. This bridges that gap, mirroring V1's
 * `fieldStateManager.markFieldDirty` on tag/custom-field edits.
 *
 * Structurally typed (not the `@ViewModels` interfaces) to stay barrel-free and
 * keep the spec lightweight. Compares field-by-field rather than via JSON.stringify
 * so it's robust to object key ordering between server-loaded and locally-built
 * entries.
 */

interface TagSettingEntry {
	fieldId: string;
	tagPrefix?: string;
	shouldGenerateTag?: boolean;
}

interface CustomFieldMapEntry {
	fieldId: string;
	contactFieldId?: string;
}

/**
 * Whether the tag-prefix or custom-field mapping for `fieldId` differs from the
 * persisted (default) state — i.e. whether the question should show as unsaved
 * because of a tag / custom-field change.
 */
export function isQuestionMappingDirty(args: {
	fieldId?: string;
	currentTagSettings?: TagSettingEntry[];
	defaultTagSettings?: TagSettingEntry[];
	currentCustomFieldMap?: CustomFieldMapEntry[];
	defaultCustomFieldMap?: CustomFieldMapEntry[];
}): boolean {
	const { fieldId } = args;
	if (!fieldId) return false;

	const curTag = (args.currentTagSettings ?? []).find(s => s.fieldId === fieldId);
	const defTag = (args.defaultTagSettings ?? []).find(s => s.fieldId === fieldId);
	// Added or removed (one side has an entry, the other doesn't).
	if (Boolean(curTag) !== Boolean(defTag)) return true;
	// Both present — compare the meaningful fields.
	if (curTag && defTag) {
		if ((curTag.tagPrefix ?? '') !== (defTag.tagPrefix ?? '')) return true;
		if (Boolean(curTag.shouldGenerateTag) !== Boolean(defTag.shouldGenerateTag)) return true;
	}

	const curMap = (args.currentCustomFieldMap ?? []).find(m => m.fieldId === fieldId);
	const defMap = (args.defaultCustomFieldMap ?? []).find(m => m.fieldId === fieldId);
	if (Boolean(curMap) !== Boolean(defMap)) return true;
	if (curMap && defMap && (curMap.contactFieldId ?? '') !== (defMap.contactFieldId ?? '')) return true;

	return false;
}
